import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import type { AdminRole, AdminRoleView } from '../types/admin';
import { isDisplayableRole, visibleAdminRoleCodes } from '../types/admin';

interface AdminRoleSelectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Available role codes from GET /admin/v1/roles; falls back to the enum. */
  roleViews: ReadonlyArray<AdminRoleView>;
  value: ReadonlyArray<AdminRole>;
  onApply: (roles: ReadonlyArray<AdminRole>) => void;
  busy?: boolean;
}

export const AdminRoleSelectDialog = ({
  open,
  onOpenChange,
  roleViews,
  value,
  onApply,
  busy = false,
}: AdminRoleSelectDialogProps) => {
  const { t } = useTranslation('common');
  const [selected, setSelected] = useState<ReadonlyArray<AdminRole>>(value);

  useEffect(() => {
    if (open) setSelected(value);
  }, [open, value]);

  const codes: ReadonlyArray<AdminRole> =
    roleViews.length > 0
      ? roleViews.map((r) => r.code as AdminRole).filter((code) => isDisplayableRole(code))
      : visibleAdminRoleCodes;

  const toggle = (role: AdminRole, checked: boolean) =>
    setSelected((prev) =>
      checked ? (prev.includes(role) ? prev : [...prev, role]) : prev.filter((r) => r !== role),
    );

  const handleApply = () => onApply(selected);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.accounts.roleDialog.title')}</DialogTitle>
          <DialogDescription>{t('admin.accounts.roleDialog.desc')}</DialogDescription>
        </DialogHeader>

        <ul className="flex max-h-[320px] flex-col gap-1 overflow-y-auto py-1">
          {codes.map((role, idx) => {
            const view = roleViews.find((r) => r.code === role);
            const rowId = `admin-role-${idx}`;
            return (
              <li
                key={role}
                className="flex items-center gap-3 rounded px-2 py-2 hover:bg-[color:var(--surface-1)]"
              >
                <Checkbox
                  id={rowId}
                  checked={selected.includes(role)}
                  onCheckedChange={(c) => toggle(role, c === true)}
                />
                <label htmlFor={rowId} className="flex flex-1 cursor-pointer flex-col">
                  <span className="text-sm" style={{ color: 'var(--content-strong)' }}>
                    {t(`admin.roles.codes.${role}`, role)}
                  </span>
                  {view?.mandatoryTwoFactor ? (
                    <span className="text-xs" style={{ color: 'var(--content-muted)' }}>
                      {t('admin.roles.cols.mandatoryTwoFactorOn')}
                    </span>
                  ) : null}
                </label>
              </li>
            );
          })}
        </ul>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              {t('admin.common.cancel')}
            </Button>
          </DialogClose>
          <Button size="sm" onClick={handleApply} disabled={busy || selected.length === 0}>
            {t('admin.common.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
